/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : DashboardPanel
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical dashboard widget panel — a Card with an H3 title
 *                  (+ optional subtitle / actions) over its content (chart,
 *                  list, KPIs). Replaces the per-dashboard ad-hoc panel divs and
 *                  local KPICard/MetricCard chrome.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { clsx } from 'clsx';
import { Info } from 'lucide-react';
import { Card } from './base';
import { AppTooltip } from './AppTooltip';

export interface DashboardPanelProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Optional hover explanation shown via a small info icon next to the title. */
  titleInfo?: React.ReactNode;
}

/** Canonical dashboard widget panel. */
export const DashboardPanel: React.FC<DashboardPanelProps> = ({ title, subtitle, actions, children, className, bodyClassName, titleInfo }) => (
  <Card className={clsx('flex h-full flex-col', className)}>
    {(title || actions) && (
      <div className="mb-3 flex items-start justify-between gap-2 border-b border-[var(--color-border)] pb-2.5">
        <div className="min-w-0">
          {title && (
            <h3 className="flex items-center text-[length:var(--text-h3)] font-bold leading-[var(--leading-h3)] tracking-tight text-[var(--color-text-primary)]">
              {title}
              {titleInfo != null && (
                <AppTooltip title={titleInfo} placement="top">
                  <span className="ml-1.5 inline-flex flex-shrink-0 cursor-help text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </AppTooltip>
              )}
            </h3>
          )}
          {subtitle && <p className="mt-0.5 text-[length:var(--text-caption)] text-[var(--color-text-secondary)]">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 items-center gap-1">{actions}</div>}
      </div>
    )}
    <div className={clsx('min-h-0 flex-1', bodyClassName)}>{children}</div>
  </Card>
);

export default DashboardPanel;
